//
// DISCLAIMER
//
// Copyright 2026 ArangoDB GmbH, Cologne, Germany
//

import { isEmpty, map } from "lodash";
import { Component } from "react";
import { RouteComponentProps } from "react-router-dom";
import { Button, Grid, Icon, Loader, Menu, Message, Segment, Table } from "semantic-ui-react";
import apiClients from "../../api/apiclients";
import {
  CreditPlan as ApiCreditPlan,
  IDOptions as ApiIDOptions,
  PriceRow as ApiPriceRow,
  PriceTable as ApiPriceTable,
  SupportPlanPrice as ApiSupportPlanPrice,
  Variable as ApiVariable,
} from "../../api/lib";
import TabLayout from "../../components/Tab/Tab";
import {
  ContentSegment,
  Field,
  FieldContent as FC,
  FieldLabel as FL,
  LoaderBox,
  MenuActionBack,
  SecondaryMenu,
  Section,
  SectionButtons,
  SectionContent,
  SectionHead,
  SectionHeader,
} from "../../ui/lib";
import { DateTimePopupWithUTCAndLocalTime } from "../../util/dateAndTimeUtils/DateTime";
import { Routes } from "../../routes";
import { IWithRefreshProps, withRefresh } from "../../util/WithRefresh";

interface IPriceTableViewArgs {
  table: ApiPriceTable;
}

// Dimension columns that a CreditPlan row may populate. They are shown only when
// at least one row in this table actually sets them — e.g. "Network transfer
// destination" appears only for the network component, "Disk performance" only
// where it is set. PricePlan-only dimensions (deployment/notebook model, node
// size, memory/disk size range, server type) are never populated here and are
// not listed. The value columns (Price / Currency / Credits) are always shown.
const dimensionColumns: { label: string; get: (r: ApiPriceRow) => string | undefined }[] = [
  { label: "Cloud provider", get: (r) => r.cloud_provider },
  { label: "Region", get: (r) => r.cloud_region },
  { label: "Network transfer destination", get: (r) => r.network_transfer_destination },
  { label: "Disk performance", get: (r) => r.disk_performance_id },
  { label: "Addon", get: (r) => r.addon_id },
];

const PriceTableView = ({ ...args }: IPriceTableViewArgs) => {
  const rows = args.table.rows || [];
  if (isEmpty(rows)) {
    return <span>No rows defined</span>;
  }
  const dims = dimensionColumns.filter((c) => rows.some((r) => !!c.get(r)));
  // Billing V2 is single-currency (USD): rows do not set currency_id, so the
  // Currency column is shown only if some row ever specifies one.
  const showCurrency = rows.some((r) => !!r.currency_id);
  return (
    <Table>
      <Table.Header>
        <Table.Row>
          {dims.map((c) => (
            <Table.HeaderCell key={c.label}>{c.label}</Table.HeaderCell>
          ))}
          <Table.HeaderCell>Price (USD)</Table.HeaderCell>
          {showCurrency && <Table.HeaderCell>Currency</Table.HeaderCell>}
          <Table.HeaderCell>Credits</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {map(rows, (x: ApiPriceRow, index: any) => (
          <Table.Row key={index}>
            {dims.map((c) => (
              <Table.Cell key={c.label}>{c.get(x) || "any"}</Table.Cell>
            ))}
            <Table.Cell>{x.price || 0}</Table.Cell>
            {showCurrency && <Table.Cell>{x.currency_id}</Table.Cell>}
            <Table.Cell>{x.credits || 0}</Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  );
};

interface IVariableViewArgs {
  name: string;
  description?: string;
  item: ApiVariable;
}

const VariableView = ({ ...args }: IVariableViewArgs) => (
  <div>
    <SecondaryMenu>
      <Menu.Item header>{args.name}</Menu.Item>
    </SecondaryMenu>
    {args.description && (
      <Message info size="small">
        {args.description}
      </Message>
    )}
    <PriceTableView table={args.item.prices || {}} />
  </div>
);

interface ISupportPlanPriceRowArgs {
  item: ApiSupportPlanPrice;
}

const SupportPlanPriceRow = ({ ...args }: ISupportPlanPriceRowArgs) => (
  <Table.Row>
    <Table.Cell>{args.item.id}</Table.Cell>
    <Table.Cell>{args.item.factor}</Table.Cell>
    <Table.Cell>{args.item.min_monthly_price}</Table.Cell>
  </Table.Row>
);

interface ISupportPlanPricesTableArgs {
  list: ApiSupportPlanPrice[];
}

const SupportPlanPricesTable = ({ ...args }: ISupportPlanPricesTableArgs) => {
  if (isEmpty(args.list)) {
    return <span>No support plan prices defined</span>;
  }
  return (
    <Table>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>Plan</Table.HeaderCell>
          <Table.HeaderCell>Factor</Table.HeaderCell>
          <Table.HeaderCell>Min. monthly price</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body
        children={map(args.list || [], (x: ApiSupportPlanPrice, index: any) => (
          <SupportPlanPriceRow key={index} item={x} />
        ))}
      />
    </Table>
  );
};

interface ICreditPlanDetailsViewArgs {
  plan: ApiCreditPlan;
}

const CreditPlanDetailsView = ({ ...args }: ICreditPlanDetailsViewArgs) => (
  <Grid width="16">
    <Grid.Column width="8">
      <Field>
        <FL>Name</FL>
        <FC>{args.plan.name || "-"}</FC>
      </Field>
      <Field>
        <FL>Description</FL>
        <FC>{args.plan.description || "-"}</FC>
      </Field>
      <Field>
        <FL>Support factor &amp; AEU discount</FL>
        <FC>{isEmpty(args.plan.revenue_usage_kinds) ? "All usage kinds (legacy), no AEU discount" : (args.plan.revenue_usage_kinds || []).join(", ")}</FC>
      </Field>
      <Field>
        <FL>Lock state</FL>
        <FC>{args.plan.locked ? "Locked" : "Unlocked"}</FC>
      </Field>
    </Grid.Column>
    <Grid.Column width="8">
      <Field>
        <FL>Created</FL>
        <FC>{args.plan.created_at ? <DateTimePopupWithUTCAndLocalTime dateTime={args.plan.created_at} label="Created at" /> : "-"}</FC>
      </Field>
      <Field>
        <FL>Starts at</FL>
        <FC>{args.plan.starts_at ? <DateTimePopupWithUTCAndLocalTime dateTime={args.plan.starts_at} label="Starts at" /> : "-"}</FC>
      </Field>
      <Field>
        <FL>Ends at</FL>
        <FC>{args.plan.ends_at ? <DateTimePopupWithUTCAndLocalTime dateTime={args.plan.ends_at} label="Ends at" /> : "open-ended"}</FC>
      </Field>
    </Grid.Column>
  </Grid>
);

const CreditPlanVariablesView = ({ ...args }: ICreditPlanDetailsViewArgs) => (
  <div>
    <TabLayout
      allowOverflow
      id="creditPlanVariablesView"
      tabClassForObserving="variablesTabView"
      tabConfig={[
        {
          menuItem: { id: "cpuHourTab", children: "CPU hour" },
          content: () => <VariableView name="Credits for each CPU per hour" item={args.plan.cpu_per_hour || {}} />,
        },
        {
          menuItem: { id: "memoryHourTab", children: "Memory hour" },
          content: () => <VariableView name="Credits for each GiB of memory per hour" item={args.plan.memory_per_gib_hour || {}} />,
        },
        {
          menuItem: { id: "deploymentAeuTab", children: "Deployment AEU" },
          content: () => (
            <VariableView
              name="Credits for each deployment AEU per hour"
              description={'AEU = "Deployment Size" × "Deployment Node Count" × "Deployment AEU Base" × "Deployment Type Ratio"'}
              item={args.plan.deployment_aeu_per_hour || {}}
            />
          ),
        },
        {
          menuItem: { id: "storageHourTab", children: "Storage hour" },
          content: () => <VariableView name="Credits for each GiB of storage per hour" item={args.plan.storage_per_gib_hour || {}} />,
        },
        {
          menuItem: { id: "storagePerformanceTab", children: "Storage performance" },
          content: () => <VariableView name="Credits for storage performance per hour" item={args.plan.storage_performance_per_hour || {}} />,
        },
        {
          menuItem: { id: "gpuHourTab", children: "GPU hour" },
          content: () => <VariableView name="Credits for each GPU per hour" item={args.plan.gpu_per_hour || {}} />,
        },
        {
          menuItem: { id: "networkTab", children: "Network" },
          content: () => <VariableView name="Credits for network usage per GiB" item={args.plan.network_per_gib || {}} />,
        },
        {
          menuItem: { id: "cloudStorageTab", children: "Cloud storage" },
          content: () => <VariableView name="Credits for cloud storage per GiB per hour" item={args.plan.cloud_storage_per_gib_hour || {}} />,
        },
        {
          menuItem: { id: "auditLogRequestsTab", children: "Audit log requests" },
          content: () => <VariableView name="Credits for each audit-log HTTPS-POST request" item={args.plan.audit_log_requests_per_invocation || {}} />,
        },
        {
          menuItem: { id: "auditLogDataTab", children: "Audit log data" },
          content: () => <VariableView name="Credits for each GiB of audit-log HTTPS-POST body" item={args.plan.audit_log_data_per_gib || {}} />,
        },
        {
          menuItem: { id: "supportPlansTab", children: "Support plans" },
          content: () => <SupportPlanPricesTable list={args.plan.support_plan_prices || []} />,
        },
        {
          menuItem: { id: "addonsTab", children: "Addons" },
          content: () => (
            <VariableView
              name="Credits for each addon per hour"
              description="Rows are matched on the addon identifier."
              item={args.plan.addon_per_hour || {}}
            />
          ),
        },
      ]}
    />
  </div>
);

// Interface decribing the properties of the credit plan view component
interface ICreditPlanDetailsProps extends IWithRefreshProps, RouteComponentProps {
  planId: string;
}

// Interface decribing the state of the credit plan view component
interface ICreditPlanDetailsState {
  plan?: ApiCreditPlan;
}

// The component to show a read-only credit plan view.
class CreditPlanDetails extends Component<ICreditPlanDetailsProps, ICreditPlanDetailsState> {
  state: ICreditPlanDetailsState = {
    plan: undefined,
  };

  reloadPlan = async () => {
    const idOptions: ApiIDOptions = { id: this.props.planId };
    var plan = await apiClients.idashboardClient.GetCreditPlan(idOptions);
    this.setState({ plan: plan });
  };

  componentDidMount() {
    this.props.refreshNow && this.props.refreshNow(this.reloadPlan);
  }

  render() {
    const plan = this.state.plan;

    return (
      <ContentSegment>
        <SecondaryMenu>
          <MenuActionBack />
          <Menu.Item header>Credit Plan Details</Menu.Item>
          <LoaderBox>
            <Loader size="mini" active={this.props.loading} inline />
          </LoaderBox>
        </SecondaryMenu>
        {plan && (
          <div>
            <Section>
              <SectionHead>
                <SectionHeader title="Details" />
                <SectionButtons>
                  <Button
                    icon
                    labelPosition="right"
                    onClick={() => this.props.history.push(Routes.dashboard_sales_creditPlan_calculatorWithId(this.props.planId))}
                  >
                    <Icon name="calculator" /> Calculator
                  </Button>
                </SectionButtons>
              </SectionHead>
              <SectionContent>
                <Segment>
                  <CreditPlanDetailsView plan={plan} />
                </Segment>
              </SectionContent>
            </Section>
            <Section>
              <SectionHead>
                <SectionHeader title="Rates" />
              </SectionHead>
              <SectionContent>
                <CreditPlanVariablesView plan={plan} />
              </SectionContent>
            </Section>
          </div>
        )}
      </ContentSegment>
    );
  }
}

export default withRefresh()(CreditPlanDetails);
